import { ButtonRestrictionType } from 'src/common/enumerations/button-restriction-type';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import { ICommandConfiguration, ICommandsGroup } from 'src/common/interfaces/dtos/ICommandsGroup';
import { IMessageModal } from 'src/common/interfaces/dtos/IMessageModal';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { ISidebarButton } from '../common/interfaces/dtos/ISidebarButton';
import { store } from '../common/redux/configure-store';
import { SidebarActions } from '../common/redux/reducers/sidebar-reducer';
import { checkCommandPermission } from '../command-menu/permissions/show-permissions-modal';
import { getSidebarButtons, MenuItem } from './sidebar-buttons';
import {
  AuthenticateCommandDto,
  authenticateTlog,
} from 'src/menu-panel/business-logic/post-authenticate-tlog';

export const setSideBarButtons = (buttons: ISidebarButton[]): void => {
  store.dispatch(SidebarActions.setButtons(buttons));
};

export const generateSideBarButtons = (posMode: string, showSidebar: boolean): ISidebarButton[] => {
  let commandGroupKey = '';
  switch (posMode) {
    case 'saleMode':
      commandGroupKey = 'SaleTransactionCommands';
      break;
    case 'returnMode':
      commandGroupKey = 'ReturnTransactionCommandGroup';
      break;
    case 'tenderMode':
      commandGroupKey = 'TenderTransactionCommands';
      break;
    case 'noSaleMode':
    default:
      commandGroupKey = 'NoSaleTransactionCommands';
      break;
  }

  return getSidebarCommandsByKey(commandGroupKey, showSidebar);
};

const getSidebarCommandsByKey = (key: string, showSidebar: boolean): ISidebarButton[] => {
  const commandMenu = store.getState().commandMenu.rawCommands;
  const menuItems = getSidebarButtons();
  const commands = commandMenu[key]?.commandsList || [];
  const sidebarCommands = sidebarButtonsMapper(commands, menuItems, showSidebar);
  return sidebarCommands;
};

const sidebarButtonsMapper = (
  commands: ICommandConfiguration[],
  menuItems: MenuItem[],
  showSidebar: boolean,
): ISidebarButton[] => {
  return commands.map(
    (command): ISidebarButton => {
      const subGroup = (command as ICommandsGroup).commandsList;
      const button = menuItems.find(button => button.key === command.name);
      if (subGroup?.length) {
        const buttonGroup = sidebarButtonsMapper(subGroup, menuItems, showSidebar);
        return {
          buttonIcon: button?.icon,
          text: command.description ?? command.name ?? '',
          handleClick: () => {},
          appliesTo: [ButtonRestrictionType.All],
          buttonGroup,
          isDisabled: !command.userHasPermission && command.allowOverride === false ? true : false,
        };
      } else {
        return {
          buttonIcon: button?.icon,
          text: command.description ?? command.name ?? '',
          handleClick: () => {
            if (button) {
              return handleCommandButtonClick(button, command);
            }
          },
          appliesTo: button?.appliesTo || [],
          isDisabled: !command.userHasPermission && command.allowOverride === false ? true : false,
        };
      }
    },
  );
};

const handleCommandButtonClick = async (button: MenuItem, command: ICommandConfiguration) => {
  if (button.handleClick) {
    if (await hasSufficientPrivileges(command)) {
      return button.handleClick(command.parameters);
    }
  }
};

export const hasSufficientPrivileges = async (command: ICommandConfiguration): Promise<boolean> => {
  if (!command.checkRole) {
    return true;
  }
  if (command.userHasPermission) {
    const tlogCreds: AuthenticateCommandDto = {
      approverId: store.getState().user.userName,
      actionLabel: command.name,
    };

    await authenticateTlog(tlogCreds);

    return true;
  }
  if (command.allowOverride) {
    return (await checkCommandPermission(command)) !== undefined;
  } else {
    const span = createSpan('insufficient-privileges');
    const insufficientPrivilegesMessage: IMessageModal = {
      message: 'InsufficientPrivileges_Message',
      subMessage: 'InsufficientPrivileges_SubMessage',
      type: EmeraldDialogTypes.Error,
      hideCancelButton: true,
      onAccept: () => {
        endSpan(span);
      },
      acceptButtonLabel: 'Ok',
    };
    popupMessage(insufficientPrivilegesMessage);
    return false;
  }
};
